<template>
  <div id="app">
    <Header />
    <main class="app-main">
      <router-view />
      <Footer />
    </main>
    <CustomerServiceChat />
    <Toast />
    <div class="youth-floating-timer" v-if="youthModeActive && !youthLocked && !isYouthProtectionRoute">
      <span>青少年模式</span>
      <strong>{{ youthCountdownText }}</strong>
    </div>
    <div class="youth-site-lock" v-if="showLockOverlay">
      <section class="youth-lock-panel">
        <span class="lock-label">青少年模式</span>
        <h2>本次使用已满 2 小时</h2>
        <p>根据当前青少年模式设置，网站已锁定。请输入设置的密码，认证通过后才能继续访问。</p>
        <div class="lock-countdown">
          <strong>{{ youthCountdownText }}</strong>
          <span>剩余可用时间</span>
        </div>
        <input
          v-model.trim="youthUnlockPassword"
          type="password"
          name="youth-unlock-code"
          autocomplete="new-password"
          placeholder="请输入密码"
          @keyup.enter="unlockYouthSite"
        >
        <button type="button" @click="unlockYouthSite">解除锁定</button>
        <p class="lock-message">{{ youthUnlockMessage }}</p>
      </section>
    </div>
    <YouthNoticeDialog
      :visible="noticeVisible"
      :title="noticeTitle"
      :description="noticeDesc"
      :type="noticeType"
      @close="noticeVisible = false"
    />
  </div>
</template>

<script>
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import CustomerServiceChat from './components/CustomerServiceChat.vue'
import YouthNoticeDialog from './components/YouthNoticeDialog.vue'
import Toast from './components/Toast.vue'
import { youthStore, initYouthStore, authYouthMode } from './utils/youthContent'

function formatCountdown(ms) {
  const total = Math.max(0, ms)
  const hours = Math.floor(total / 3600000)
  const minutes = Math.floor((total % 3600000) / 60000)
  const seconds = Math.floor((total % 60000) / 1000)
  return [hours, minutes, seconds].map(item => String(item).padStart(2, '0')).join(':')
}

export default {
  name: 'App',
  components: {
    Header,
    Footer,
    CustomerServiceChat,
    YouthNoticeDialog,
    Toast
  },
  data() {
    return {
      youthUnlockPassword: '',
      youthUnlockMessage: '请输入密码解除锁定。',
      noticeVisible: false,
      noticeTitle: '',
      noticeDesc: '',
      noticeType: 'success'
    }
  },
  computed: {
    youthModeActive() {
      return youthStore.enabled
    },
    youthLocked() {
      return youthStore.enabled && youthStore.locked
    },
    youthCountdownText() {
      return formatCountdown(youthStore.remaining)
    },
    isYouthProtectionRoute() {
      return this.$route.path === '/youth-protection'
    },
    showLockOverlay() {
      return this.youthLocked && !this.isYouthProtectionRoute
    }
  },
  watch: {
    showLockOverlay: {
      immediate: true,
      handler(show) {
        document.body.style.overflow = show ? 'hidden' : ''
        if (show) {
          this.youthUnlockPassword = ''
          this.youthUnlockMessage = youthStore.hasGuardianCode
            ? '请输入密码解除锁定。'
            : '当前没有设置密码，请联系管理员处理。'
          // 锁定后离开当前页面，只保留青少年模式设置页
          this.$router.replace('/youth-protection').catch(() => {})
        } else {
          this.youthUnlockMessage = '请输入密码解除锁定。'
        }
      }
    }
  },
  mounted() {
    initYouthStore()
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
  methods: {
    async unlockYouthSite() {
      if (!youthStore.hasGuardianCode) {
        this.youthUnlockMessage = '当前没有设置密码，请联系管理员处理。'
        return
      }
      if (!this.youthUnlockPassword) {
        this.youthUnlockMessage = '请输入密码。'
        return
      }

      this.youthUnlockMessage = '正在认证...'
      try {
        await authYouthMode(this.youthUnlockPassword)
        this.youthUnlockPassword = ''
        this.youthUnlockMessage = '密码认证通过，青少年模式已关闭。'
        // 解锁成功后弹出醒目提示
        this.noticeTitle = '青少年模式已关闭'
        this.noticeDesc = '密码认证通过，本账号已退出青少年模式，年龄权限限制已解除。'
        this.noticeType = 'info'
        this.noticeVisible = true
      } catch (error) {
        this.youthUnlockMessage = (error && error.message) || '密码错误，请重新输入。'
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Inter", "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  background:
    radial-gradient(circle at 18% 8%, rgba(255, 182, 72, 0.16), transparent 26%),
    radial-gradient(circle at 86% 12%, rgba(35, 211, 238, 0.13), transparent 30%),
    radial-gradient(circle at 68% 86%, rgba(126, 87, 255, 0.18), transparent 36%),
    linear-gradient(180deg, #070912 0%, #101321 46%, #090b13 100%);
  color: #eef3ff;
  min-width: 320px;
}

#app {
  width: 100%;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #24304f;
}

.app-main {
  min-height: 100vh;
  margin-left: 236px;
  transition: margin-left 0.25s ease;
}

a {
  text-decoration: none;
}

img {
  max-width: 100%;
}

button,
input {
  font-family: inherit;
}

::selection {
  background: rgba(255, 204, 102, 0.34);
  color: #fff;
}

body::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.72), transparent 78%);
}

.app-main :where(.container, .category-content, .vip-page, .about-us .container, .user-center .container, .copyright-page .container) {
  max-width: 1500px;
}

.app-main :where(.section-header, .about-hero, .about-card, .business-card, .vip-header, .vip-container, .profile-brand-panel, .user-header, .feature-section, .category-title, .filter-section) {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.9), rgba(12, 15, 27, 0.92));
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
}

.app-main :where(h1, h2, h3, h4, .comic-title, .category-heading, .nickname, .package-name, .benefit-title, .plan-duration, .form-title) {
  color: #f5f7ff;
}

.app-main :where(p, span, .comic-author, .comic-desc, .lead, .benefit-desc, .package-desc, .package-duration, .user-id, .user-join-time, .user-bio, .stat-label, .empty-content p, .loading-state, .no-data, .vip-slogan) {
  color: #9faaca;
}

.app-main :where(button, .more, .comic-btn, .payment-btn, .primary-btn, .brand-action, .plan-btn, .vip-btn) {
  border-radius: 8px;
}

.app-main :where(input, textarea) {
  color: #eef3ff;
  background: rgba(7, 10, 18, 0.82);
  border-color: rgba(255, 255, 255, 0.12);
}

.youth-floating-timer {
  position: fixed;
  right: 20px;
  bottom: 22px;
  z-index: 2400;
  display: grid;
  gap: 2px;
  min-width: 168px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 204, 102, 0.28);
  border-radius: 8px;
  color: #eef3ff;
  background: rgba(12, 15, 27, 0.94);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.36);
}

.youth-floating-timer span {
  color: #23d3ee;
  font-size: 12px;
  font-weight: 900;
}

.youth-floating-timer strong {
  color: #ffcc66;
  font-size: 22px;
  letter-spacing: 0;
}

.youth-site-lock {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(4, 7, 14, 0.88);
  backdrop-filter: blur(10px);
}

.youth-lock-panel {
  width: min(440px, 100%);
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: #eef3ff;
  background:
    radial-gradient(circle at 82% 0%, rgba(255, 204, 102, 0.16), transparent 34%),
    linear-gradient(180deg, rgba(20, 24, 39, 0.98), rgba(9, 12, 22, 0.98));
  box-shadow: 0 32px 86px rgba(0, 0, 0, 0.46);
}

.lock-label {
  color: #23d3ee;
  font-size: 12px;
  font-weight: 900;
}

.youth-lock-panel h2 {
  margin: 8px 0 10px;
  color: #f5f7ff;
  font-size: 28px;
  line-height: 1.18;
}

.youth-lock-panel p {
  color: #9faaca;
  line-height: 1.7;
}

.lock-countdown {
  display: grid;
  gap: 2px;
  margin: 18px 0;
  padding: 16px;
  border: 1px solid rgba(255, 204, 102, 0.25);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.lock-countdown strong {
  color: #ffcc66;
  font-size: 40px;
  line-height: 1;
}

.lock-countdown span {
  color: #9faaca;
  font-size: 13px;
}

.youth-lock-panel input {
  width: 100%;
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 0 13px;
  color: #eef3ff;
  outline: none;
  background: rgba(7, 10, 18, 0.82);
}

.youth-lock-panel input:focus {
  border-color: #23d3ee;
  box-shadow: 0 0 0 4px rgba(35, 211, 238, 0.12);
}

.youth-lock-panel button {
  width: 100%;
  min-height: 44px;
  margin-top: 12px;
  border: 0;
  border-radius: 8px;
  color: #07101d;
  font-weight: 900;
  cursor: pointer;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.youth-lock-panel .lock-message {
  min-height: 22px;
  margin-top: 10px;
  color: #ffcc66;
  font-size: 13px;
}

.comic-homepage,
.full-page,
.category-container,
.vip-page,
.about-us,
.user-center,
.comic-detail-page,
.copyright-page {
  background:
    radial-gradient(circle at 16% 0%, rgba(255, 204, 102, 0.12), transparent 32%),
    radial-gradient(circle at 84% 16%, rgba(35, 211, 238, 0.1), transparent 30%),
    linear-gradient(180deg, rgba(7, 9, 18, 0.2), rgba(7, 9, 18, 0.68)) !important;
}

.comic-homepage .container,
.category-content,
.vip-page,
.about-us .container,
.user-center .container,
.comic-detail-page .container,
.copyright-page .container {
  padding-left: 32px;
  padding-right: 32px;
}

.comic-homepage .swiper,
.comic-homepage .comic-item,
.comic-homepage .latest-card,
.comic-homepage .completed-card,
.category-content .comic-item,
.history-card,
.comment-item,
.package-item,
.plan-item,
.benefit-item,
.business-groups div,
.info-item,
.settings-form {
  border-radius: 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  background: linear-gradient(180deg, rgba(22, 26, 43, 0.96), rgba(11, 14, 25, 0.98)) !important;
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.28) !important;
}

.comic-homepage .swiper {
  height: clamp(360px, 39vw, 620px) !important;
  border-radius: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

.comic-homepage .section-header {
  padding: 18px 22px !important;
  background: transparent !important;
  border-left: 0 !important;
  border-right: 0 !important;
  box-shadow: none !important;
}

.comic-homepage .section-header h2,
.category-heading {
  font-size: clamp(28px, 3vw, 44px) !important;
  line-height: 1.08;
}

.comic-homepage .section-header h2::after,
.category-heading::before {
  background: linear-gradient(135deg, #ffcc66, #23d3ee) !important;
}

.comic-homepage .more,
.comic-btn,
.payment-btn,
.primary-btn,
.brand-action,
.plan-item.active .plan-btn,
.package-selected {
  color: #07101d !important;
  background: linear-gradient(135deg, #ffcc66, #23d3ee) !important;
  box-shadow: 0 14px 30px rgba(255, 204, 102, 0.18) !important;
  border: 0 !important;
}

.comic-homepage .comic-list,
.comic-homepage .latest-grid,
.comic-homepage .completed-grid,
.comics-grid {
  gap: 20px !important;
}

.comic-homepage .comic-cover,
.category-content .comic-cover,
.history-card-cover,
.comment-cover {
  border-radius: 8px !important;
  overflow: hidden;
  background: #070912 !important;
}

.comic-homepage .comic-cover img,
.category-content .comic-cover img,
.history-card-cover img,
.comment-cover img {
  filter: saturate(1.05) contrast(1.04);
}

.category-container {
  padding: 34px 0 64px !important;
}

.category-title {
  padding: 28px !important;
  margin-bottom: 26px !important;
}

.category-heading {
  padding-left: 0 !important;
}

.category-heading::before {
  top: auto !important;
  bottom: -12px !important;
  width: 76px !important;
  height: 5px !important;
  border-radius: 999px !important;
  transform: none !important;
}

.category-content .comic-item {
  padding: 10px !important;
  margin-bottom: 0 !important;
}

.category-content .comic-title,
.history-title,
.comment-header .comic-title,
.benefit-name,
.plan-price,
.package-price {
  color: #f5f7ff !important;
}

.category-content .comic-author,
.history-chapter,
.history-time,
.comment-content,
.benefit-desc,
.plan-desc,
.package-desc {
  color: #9faaca !important;
}

.vip-header,
.profile-brand-panel,
.about-hero {
  background:
    radial-gradient(circle at 78% 18%, rgba(255, 204, 102, 0.18), transparent 30%),
    linear-gradient(135deg, rgba(25, 30, 49, 0.96), rgba(10, 13, 24, 0.98)) !important;
}

.vip-brand-logo,
.profile-brand-logo,
.about-logo,
.footer-logo {
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.92) !important;
}

.vip-benefits,
.vip-card,
.user-header-backdrop {
  background:
    radial-gradient(circle at 88% 18%, rgba(35, 211, 238, 0.22), transparent 30%),
    linear-gradient(135deg, #111827, #080b14) !important;
}

.feature-tabs {
  background: rgba(5, 8, 16, 0.82) !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.tab {
  color: #9faaca !important;
}

.tab.active,
.tab:hover,
.eyebrow,
.business-groups h3,
.info-item span {
  color: #23d3ee !important;
}

.tab.active::after {
  background: linear-gradient(135deg, #ffcc66, #23d3ee) !important;
}

.stat-item .stat-value,
.form-group label,
.vip-status h3,
.vip-status p,
.package-info h4 {
  color: #f5f7ff !important;
}

.user-stats,
.user-bio,
.vip-benefits,
.settings-form {
  background: rgba(255, 255, 255, 0.05) !important;
}

.payment-modal {
  background: rgba(0, 0, 0, 0.72) !important;
}

.payment-content {
  border-radius: 10px !important;
  color: #eef3ff !important;
  background: #111525 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.payment-header {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.qrcode-container img {
  background: #fff;
}

@media (max-width: 920px) {
  .app-main {
    margin-left: 0;
  }

  .comic-homepage .container,
  .category-content,
  .vip-page,
  .about-us .container,
  .user-center .container,
  .comic-detail-page .container,
  .copyright-page .container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .youth-floating-timer {
    right: 14px;
    bottom: 14px;
    min-width: 148px;
  }

  .youth-lock-panel {
    padding: 20px;
  }
}
</style>
